import { useEffect, useRef, useState } from "react";

export interface UseFileContextTriggerResult {
  // State
  isOpen: boolean;
  searchQuery: string;
  dropdownPosition: { top: number; left: number } | null;

  // Handlers to attach to textarea
  onTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onTextareaKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;

  // Actions
  onFileSelect: (filePath: string) => void;
  onClose: () => void;
  openPicker: () => void;
}

/**
 * Custom hook for @ file context trigger functionality.
 * Detects @ character in textarea, manages dropdown state, and handles file selection.
 *
 * @param prompt - Current prompt value
 * @param setPrompt - Function to update prompt
 * @param existingTextareaRef - Optional textarea ref from parent component
 * @param addFileReference - Function to add file reference to array (pill display)
 * @returns Hook result with state and handlers
 */
export function useFileContextTrigger(
  prompt: string | null,
  setPrompt: (prompt: string) => void,
  existingTextareaRef?: React.RefObject<HTMLTextAreaElement>,
  addFileReference?: (filePath: string) => void
): UseFileContextTriggerResult {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [atTriggerPosition, setAtTriggerPosition] = useState<number | null>(
    null
  );
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Internal textarea ref (used if not provided by parent)
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = existingTextareaRef || internalTextareaRef;

  /**
   * Detects @ character and opens file context picker
   */
  const onTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;

    // Check if @ was just typed
    if (newValue[cursorPosition - 1] === "@") {
      // Only trigger if @ is at start or preceded by whitespace
      const charBefore =
        cursorPosition >= 2 ? newValue[cursorPosition - 2] : " ";
      const isValidTrigger =
        charBefore === " " ||
        charBefore === "\n" ||
        charBefore === "\t" ||
        cursorPosition === 1;

      if (isValidTrigger) {
        setAtTriggerPosition(cursorPosition - 1);
        setSearchQuery("");
        setIsOpen(true);

        // Calculate dropdown position
        const textareaElement = textareaRef.current;
        if (textareaElement) {
          const textareaRect = textareaElement.getBoundingClientRect();

          // Position dropdown above textarea with 8px gap
          const dropdownHeight = 400;
          const spaceAbove = textareaRect.top;

          // Check if there's enough space above
          const positionAbove = spaceAbove >= dropdownHeight + 16;

          setDropdownPosition({
            top: positionAbove
              ? textareaRect.top - dropdownHeight - 8
              : textareaRect.bottom + 8,
            left: textareaRect.left,
          });
        }
      }
    }

    // Update search query if dropdown is open
    if (isOpen && atTriggerPosition !== null) {
      const textAfterAt = newValue.slice(atTriggerPosition + 1, cursorPosition);

      // Close if @ was deleted
      if (!newValue[atTriggerPosition] || newValue[atTriggerPosition] !== "@") {
        setIsOpen(false);
        setAtTriggerPosition(null);
        setSearchQuery("");
      } else {
        setSearchQuery(textAfterAt);
      }
    }
  };

  /**
   * Handles keyboard events (Escape to close)
   */
  const onTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      onClose();
    }
  };

  /**
   * Handles file selection - inserts @file:... inline in textarea
   */
  const onFileSelect = (filePath: string) => {
    if (!textareaRef.current) return;

    const currentValue = prompt || "";
    const fileReference = `@file:${filePath}`;

    // Case 1: Triggered via @ character - replace @ and search query
    if (atTriggerPosition !== null) {
      const cursorPosition = textareaRef.current.selectionStart;

      // Build new prompt with inline file reference
      const beforeAt = currentValue.slice(0, atTriggerPosition);
      const afterSearch = currentValue.slice(cursorPosition); // Skip @ and search query

      const newValue = beforeAt + fileReference + afterSearch;
      setPrompt(newValue);

      // Position cursor after the inserted file path
      const newCursorPos = atTriggerPosition + fileReference.length;
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    }
    // Case 2: Opened via menu button - insert at current cursor position
    else {
      const cursorPosition = textareaRef.current.selectionStart || currentValue.length;

      // Insert file reference at cursor position
      const before = currentValue.slice(0, cursorPosition);
      const after = currentValue.slice(cursorPosition);

      // Add space before if needed (cursor not at start and previous char isn't whitespace)
      const needsSpaceBefore = cursorPosition > 0 && !/\s$/.test(before);
      const prefix = needsSpaceBefore ? ' ' : '';

      const newValue = before + prefix + fileReference + after;
      setPrompt(newValue);

      // Position cursor after the inserted file path
      const newCursorPos = cursorPosition + prefix.length + fileReference.length;
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    }

    // Close dropdown
    setIsOpen(false);
    setAtTriggerPosition(null);
    setSearchQuery("");
  };

  /**
   * Closes the file context picker and restores focus to textarea
   */
  const onClose = () => {
    setIsOpen(false);
    setAtTriggerPosition(null);
    setSearchQuery("");

    // Restore focus to textarea when closing (e.g., via ESC key)
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  /**
   * Opens the file context picker programmatically (e.g., from menu button)
   */
  const openPicker = () => {
    const textareaElement = textareaRef.current;
    if (!textareaElement) return;

    // Set trigger position to null (no @ character to remove on selection)
    setAtTriggerPosition(null);
    setSearchQuery("");
    setIsOpen(true);

    // Calculate dropdown position
    const textareaRect = textareaElement.getBoundingClientRect();
    const dropdownHeight = 400;
    const spaceAbove = textareaRect.top;
    const positionAbove = spaceAbove >= dropdownHeight + 16;

    setDropdownPosition({
      top: positionAbove
        ? textareaRect.top - dropdownHeight - 8
        : textareaRect.bottom + 8,
      left: textareaRect.left,
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsOpen(false);
      setAtTriggerPosition(null);
      setSearchQuery("");
    };
  }, []);

  return {
    isOpen,
    searchQuery,
    dropdownPosition,
    onTextareaChange,
    onTextareaKeyDown,
    onFileSelect,
    onClose,
    openPicker,
  };
}
